import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DollarSign, CheckCircle, Clock, FileText, AlertCircle } from 'lucide-react';
import { paymentCalculator } from '../../services/PaymentCalculator';
import { useAuth } from '../../contexts/AuthContext';

export default function PaymentApprovalModal({ payment, onClose, onApprove }) {
    const { currentUser } = useAuth();
    const [approvalNote, setApprovalNote] = useState('');
    const [processing, setProcessing] = useState(false);
    const [action, setAction] = useState('approve'); // 'approve' or 'paid'

    const handleApprove = async () => {
        setProcessing(true);

        try {
            await paymentCalculator.approvePayment(
                payment.id,
                currentUser.uid,
                approvalNote
            );

            alert('✅ Payment approved!');

            if (onApprove) {
                onApprove();
            }

            onClose();

        } catch (error) {
            console.error('Error approving payment:', error);
            alert('Failed to approve payment. Check console for details.');
        } finally {
            setProcessing(false);
        }
    };

    const handleMarkAsPaid = async () => {
        setProcessing(true);

        try {
            await paymentCalculator.markAsPaid(
                payment.id,
                currentUser.uid,
                'Manual',
                approvalNote
            );

            alert('✅ Payment marked as paid!');

            if (onApprove) {
                onApprove();
            }

            onClose();

        } catch (error) {
            console.error('Error marking payment as paid:', error);
            alert('Failed to mark payment as paid. Check console for details.');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            'pending': 'bg-yellow-100 text-yellow-800',
            'approved': 'bg-blue-100 text-blue-800',
            'paid': 'bg-green-100 text-green-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white rounded-xl shadow-xl max-w-2xl w-full"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    Payment Approval
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {payment.contractorName}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                        {/* Payment Amount */}
                        <div className="text-center p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
                            <p className="text-sm text-gray-600 mb-2">Payment Amount</p>
                            <p className="text-5xl font-bold text-gray-900">
                                ${payment.calculatedAmount?.toFixed(2) || '0.00'}
                            </p>
                            {payment.hoursWorked && (
                                <p className="text-sm text-gray-600 mt-2">
                                    {payment.hoursWorked} hours × ${payment.hourlyRate}/hr
                                </p>
                            )}
                        </div>

                        {/* Status Badge */}
                        <div className="flex items-center justify-center">
                            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(payment.status)}`}>
                                {payment.status === 'pending' && <Clock className="w-4 h-4 inline mr-2" />}
                                {payment.status === 'approved' && <CheckCircle className="w-4 h-4 inline mr-2" />}
                                {payment.status.toUpperCase()}
                            </span>
                        </div>

                        {/* Job Details */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="font-semibold text-gray-900 mb-3">Job Details</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-gray-600">Service Type</p>
                                    <p className="font-medium text-gray-900">
                                        {payment.jobType || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-gray-600">Location</p>
                                    <p className="font-medium text-gray-900">
                                        {payment.jobLocation || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-gray-600">Created</p>
                                    <p className="font-medium text-gray-900">
                                        {new Date(payment.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-gray-600">Payment Type</p>
                                    <p className="font-medium text-gray-900">
                                        {payment.paymentType === 'fixed' ? 'Fixed Amount' : 'Hourly'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Approval Note */}
                        <div>
                            <label className="block font-semibold text-gray-900 mb-2">
                                Note (Optional)
                            </label>
                            <textarea
                                value={approvalNote}
                                onChange={(e) => setApprovalNote(e.target.value)}
                                placeholder="Add any notes about this payment..."
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>

                        {/* Warning if already approved */}
                        {payment.status === 'approved' && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start space-x-3">
                                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium text-blue-900">Already Approved</p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            This payment was approved on {new Date(payment.approvedAt).toLocaleDateString()}.
                                            You can mark it as paid below.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>

                        {payment.status === 'pending' && (
                            <button
                                onClick={handleApprove}
                                disabled={processing}
                                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {processing ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>Approving...</span>
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        <span>Approve Payment</span>
                                    </>
                                )}
                            </button>
                        )}

                        {payment.status === 'approved' && (
                            <button
                                onClick={handleMarkAsPaid}
                                disabled={processing}
                                className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                                {processing ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    <>
                                        <DollarSign className="w-4 h-4" />
                                        <span>Mark as Paid</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
